import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import TemplateCard from './TemplateCard';

const ScenarioList = ({
  templates,
  categories,
  search,
  setSearch,
  sortMode,
  setSortMode,
  listLoading,
  importing,
  availableColors,
  openPaletteId,
  setOpenPaletteId,
  palettePreview,
  setPalettePreview,
  onCategoryEdit,
  onCategoryDelete,
  onCategoryToggle,
  onCategoryColorChange,
  onScenarioRemove,
  onTemplatePreview,
  onTemplateDelete,
  onTemplateDuplicate,
  onImportTemplates,
  onShowCategoryModal
}) => {
  const navigate = useNavigate();

  // Calculate unassigned templates
  const assignedIds = categories.flatMap(cat => cat.sheets || []);
  const unassignedTemplateIds = templates
    .filter(t => !assignedIds.includes(t.id))
    .filter(t =>
      t.name.toLowerCase().includes(search.toLowerCase()) ||
      (t.description && t.description.toLowerCase().includes(search.toLowerCase()))
    )
    .map(t => t.id);

  return (
    <div className="min-h-screen bg-bg text-text flex flex-col items-center py-8">
      <h1 className="text-2xl font-bold mb-4 text-primary">🎭 Scenario Builder</h1>
      
      {/* Categories and Organization Section */}
      <div className="w-full max-w-5xl bg-table p-6 rounded-xl border border-primary mb-6 relative">
        {/* Unassigned sidebar */}
        <Droppable droppableId="unassigned">
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="w-48 flex-shrink-0 bg-white/80 border border-gray-200 rounded-xl p-2 mr-4 min-h-[60px] float-left"
              style={{ background: snapshot.isDraggingOver ? '#dbeafe' : undefined }}
            >
              <div className="font-semibold mb-2 text-center">Unassigned</div>
              {unassignedTemplateIds.length === 0 ? (
                <div className="text-xs text-gray-400 text-center">No unassigned scenarios</div>
              ) : (
                <ul className="space-y-2">
                  {unassignedTemplateIds.map((tid, idx) => {
                    const t = templates.find(tt => tt.id === tid);
                    if (!t) return null;
                    return (
                      <Draggable key={tid} draggableId={tid} index={idx}>
                        {(provided, snapshot) => (
                          <li
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className={`bg-gray-100 rounded p-2 text-xs cursor-pointer hover:bg-gray-200 transition-colors duration-200 ${snapshot.isDragging ? 'bg-blue-200 shadow-lg' : ''}`}
                            onClick={() => navigate(`/scenario-builder/${tid}`)}
                          >
                            {t.name || '(Untitled)'}
                          </li>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </ul>
              )}
            </div>
          )}
        </Droppable>

        {/* Main content */}
        <div className="flex-1 overflow-hidden">
          <div className="flex items-center gap-4 mb-4 flex-wrap">
            <button
              className="bg-primary text-white px-4 py-2 rounded shadow hover:bg-secondary hover:text-primary font-semibold transition duration-200"
              onClick={onShowCategoryModal}
            >
              + Category
            </button>
            <button
              className="bg-primary text-white px-4 py-2 rounded shadow hover:bg-secondary hover:text-primary font-semibold transition duration-200"
              onClick={() => {
                console.log('New Scenario button clicked, navigating to /scenario-builder/new');
                navigate('/scenario-builder/new');
              }}
            >
              + New Scenario
            </button>
            <input
              type="text"
              placeholder="Search scenarios..."
              className="border rounded px-2 py-1 ml-auto"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <select className="border rounded px-2 py-1" value={sortMode} onChange={e => setSortMode(e.target.value)}>
              <option value="alpha">Sort: A-Z</option>
              <option value="custom">Sort: Custom</option>
            </select>
          </div>

          {/* Category list */}
          <div className="space-y-4">
            {categories.length === 0 && <div className="text-gray-400 italic">No categories yet.</div>}
            {categories.map(cat => {
              const previewColor = palettePreview[cat.id] || cat.color || '#3b82f6';
              return (
                <Droppable key={cat.id} droppableId={`cat-${cat.id}`}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className="border rounded-xl p-2 bg-white/90 shadow relative category-container transition-all duration-200"
                      style={{ 
                        borderColor: previewColor, 
                        background: snapshot.isDraggingOver ? '#bbf7d0' : undefined, 
                        color: previewColor 
                      }}
                    >
                      <div
                        className="flex items-center w-full text-center font-semibold text-lg justify-center cursor-pointer select-none category-header transition-colors duration-200"
                        style={{ color: previewColor }}
                        role="button"
                        tabIndex={0}
                        onClick={e => {
                          if (e.target.closest('.palette-trigger')) return;
                          onCategoryToggle(cat.id);
                        }}
                        onKeyDown={e => {
                          if ((e.key === 'Enter' || e.key === ' ') && !e.target.closest('.palette-trigger')) {
                            onCategoryToggle(cat.id);
                          }
                        }}
                      >
                        <span className="flex-1 text-center category-title">{cat.name}</span>
                        
                        {/* Color picker */}
                        <div className="palette-trigger ml-2 flex items-center relative">
                          <svg
                            viewBox="0 0 24 24"
                            className="w-6 h-6 cursor-pointer transition-transform duration-200 hover:scale-110"
                            onClick={e => {
                              e.stopPropagation();
                              setOpenPaletteId(cat.id === openPaletteId ? null : cat.id);
                              setPalettePreview({});
                            }}
                            title="Change category color"
                          >
                            <circle
                              cx="12"
                              cy="12"
                              r="9"
                              fill={previewColor}
                              stroke="#888"
                              strokeWidth="1.5"
                            />
                          </svg>
                          
                          {/* Color picker popover */}
                          {openPaletteId === cat.id && (
                            <div
                              className="absolute z-50 left-1/2 -translate-x-1/2 mt-2 bg-white border border-gray-300 rounded-lg shadow-lg px-2 py-1 grid grid-cols-3 gap-1 min-w-[110px] justify-center animate-in fade-in zoom-in-95 duration-200"
                              style={{ top: '100%' }}
                              onClick={e => e.stopPropagation()}
                            >
                              {availableColors.map(color => (
                                <div
                                  key={color}
                                  className="w-6 h-6 rounded cursor-pointer border border-gray-300 transition-all duration-200 hover:scale-110"
                                  style={{
                                    backgroundColor: color,
                                    boxShadow: color === previewColor ? '0 0 0 2px #3b82f6' : undefined,
                                  }}
                                  onMouseEnter={() => setPalettePreview({ [cat.id]: color })}
                                  onMouseLeave={() => setPalettePreview({})}
                                  onClick={() => {
                                    onCategoryColorChange(cat.id, cat.name, color);
                                    setOpenPaletteId(null);
                                    setPalettePreview({});
                                  }}
                                  aria-label={`Set color ${color}`}
                                />
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Edit icon */}
                        <button 
                          className="ml-2 transition-colors duration-200 hover:text-blue-600" 
                          onClick={e => { 
                            e.stopPropagation(); 
                            onCategoryEdit(cat); 
                          }} 
                          title="Edit Category"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>

                        {/* Delete icon */}
                        <button 
                          className="ml-1 transition-colors duration-200 hover:text-red-600" 
                          onClick={e => { 
                            e.stopPropagation(); 
                            onCategoryDelete(cat.id); 
                          }} 
                          title="Delete Category"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>

                      {/* Category content (collapsed/expanded) */}
                      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                        cat.expanded ? 'max-h-96 opacity-100 mt-2' : 'max-h-0 opacity-0'
                      }`}>
                        <ul className="space-y-1">
                          {(cat.sheets || []).map(tid => {
                            const t = templates.find(tt => tt.id === tid);
                            if (!t) return null;
                            return (
                              <li key={tid} className="flex items-center justify-between text-sm bg-white/50 rounded p-1 transition-colors duration-200 hover:bg-white/70">
                                <span 
                                  className="cursor-pointer hover:underline transition-colors duration-200" 
                                  onClick={() => navigate(`/scenario-builder/${tid}`)}
                                >
                                  {t.name}
                                </span>
                                <button
                                  className="text-red-500 hover:text-red-700 text-xs ml-2 px-1 transition-colors duration-200"
                                  title="Remove from category"
                                  onClick={() => onScenarioRemove(cat.id, tid)}
                                  aria-label="Remove scenario from category"
                                >
                                  &minus;
                                </button>
                              </li>
                            );
                          })}
                          {(cat.sheets || []).length === 0 && (
                            <li className="text-xs text-gray-400 italic">No scenarios in this category.</li>
                          )}
                        </ul>
                      </div>
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              );
            })}
          </div>
        </div>
      </div>

      {/* Template list and actions */}
      <div className="w-full max-w-5xl bg-white p-6 rounded-xl border border-primary shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">All Scenarios</h2>
          <div className="flex gap-2">
            <button
              className="bg-green-500 text-white px-4 py-2 rounded shadow hover:bg-green-700 font-semibold transition duration-200"
              onClick={onImportTemplates}
              disabled={importing}
            >
              {importing ? 'Importing...' : 'Import Defaults'}
            </button>
          </div>
        </div>
        
        {listLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <div className="mt-2">Loading scenarios...</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates
              .filter(t => 
                t.name.toLowerCase().includes(search.toLowerCase()) ||
                (t.description && t.description.toLowerCase().includes(search.toLowerCase()))
              )
              .map(template => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onPreview={onTemplatePreview}
                  onDelete={onTemplateDelete}
                  onDuplicate={onTemplateDuplicate}
                />
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScenarioList;
